import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Alert, 
  Spinner,
  Badge 
} from "react-bootstrap";
import { 
  CheckCircle, 
  Person, 
  GeoAlt, 
  Scissors, 
  Clock, 
  CreditCard,
  Building 
} from "react-bootstrap-icons";
import axios from "axios";
import API_BASE_URL from "../config";

export default function BookingSuccess() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get(`${API_BASE_URL}/bookings/${bookingId}`);
        setBooking(res.data);
      } catch (err) {
        setError("Failed to load booking details.");
      }
    }
    load();
  }, [bookingId]);

  if (error)
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={8}>
            <Alert variant="danger">
              <CheckCircle className="me-2" />
              {error}
            </Alert>
          </Col>
        </Row>
      </Container>
    );

  if (!booking)
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" size="sm" className="me-2" />
        <div>Loading booking details...</div>
      </Container>
    );

  const displayAddr = booking.branch || booking.company;

  return (
    <Container className="py-3 px-3 px-md-4">
      {/* ✅ Success Header */}
      <Row className="justify-content-center mb-4">
        <Col xs={12} md={8}>
          <Card className="text-center border-0 shadow-sm bg-success bg-opacity-10">
            <Card.Body>
              <CheckCircle className="text-success mb-3" size={64} />
              <Card.Title as="h2" className="text-success mb-1">
                Booking Confirmed!
              </Card.Title>
              <Card.Text className="mb-0">
                <Badge bg="success" className="fs-6">
                  ID: #{booking.id}
                </Badge>
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        {/* ✅ Customer Details Card */}
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <Card.Title className="d-flex align-items-center mb-3">
                <Person className="me-2 text-primary" />
                Customer Details
              </Card.Title>
              <div className="mb-2">
                <strong>{booking.customer?.name}</strong>
              </div>
              <div className="small text-muted mb-1">Email</div>
              <div className="mb-2">{booking.customer?.email}</div>
              <div className="small text-muted mb-1">Phone</div>
              <div>{booking.customer?.phone}</div>
            </Card.Body>
          </Card>
        </Col>

        {/* ✅ Location Card */}
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <Card.Title className="d-flex align-items-center mb-3">
                <GeoAlt className="me-2 text-info" />
                Location
              </Card.Title>
              <div className="mb-2">
                <strong>{displayAddr.name}</strong>
              </div>
              {displayAddr.address && (
                <>
                  <div className="small text-muted mb-1">Address</div>
                  <div className="mb-2">{displayAddr.address}</div>
                </>
              )}
              {displayAddr.landmark && (
                <>
                  <div className="small text-muted mb-1">Landmark</div>
                  <div className="mb-2">{displayAddr.landmark}</div>
                </>
              )}
              <div className="mb-2">
                <strong>{displayAddr.city}, {displayAddr.state}</strong>
              </div>
              {displayAddr.email && (
                <>
                  <div className="small text-muted mb-1">Email</div>
                  <div className="mb-2">{displayAddr.email}</div>
                </>
              )}
              {displayAddr.phone && (
                <>
                  <div className="small text-muted mb-1">Phone</div>
                  <div>{displayAddr.phone}</div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* ✅ Services Table Card */}
        <Col xs={12}>
          <Card className="shadow-sm border-0">
            <Card.Body>
              <Card.Title className="d-flex align-items-center mb-3">
                <Scissors className="me-2 text-warning" />
                Booked Services
              </Card.Title>
              <div className="table-responsive">
                <Table striped hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="col-4">Service</th>
                      <th className="col-3 d-none d-md-table-cell">Employee</th>
                      <th className="col-2 d-none d-lg-table-cell">Start</th>
                      <th className="col-2 d-none d-lg-table-cell">End</th>
                      <th className="col-1">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {booking.items.map((i) => (
                      <tr key={i.id}>
                        <td>
                          <strong>{i.service_name}</strong>
                        </td>
                        <td className="d-none d-md-table-cell">
                          {i.employee_name || "—"}
                        </td>
                        <td className="d-none d-lg-table-cell small">
                          {new Date(i.scheduled_start).toLocaleString()}
                        </td>
                        <td className="d-none d-lg-table-cell small">
                          {new Date(i.scheduled_end).toLocaleString()}
                        </td>
                        <td>
                          <strong>₹{(i.price_cents || i.service_price_cents || 0).toFixed(2)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ✅ Total Amount */}
      <Row className="mt-4 justify-content-end">
        <Col xs={12} md={4} className="text-end">
          <Card className="bg-primary bg-opacity-10 border-0">
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-end">
                <CreditCard className="me-2 text-primary" size={24} />
                <div>
                  <div className="small text-muted">Total Amount</div>
                  <div className="h4 mb-0 text-primary fw-bold">
                    ₹{booking.total_amount_cents.toFixed(2)}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}